import { useMemo, useState } from 'react'
import useInputDebounce from 'hooks/useInputDebounce'

export type ComboBoxOption<T> = {
	label: string,
	value: T,
};

export const DEFAULT_DEBOUNCE_DELAY_MS = 400

export type ComboBoxSearchArgs = {
	setSearchString?: ( search: string ) => void,
	debounceDelayMs?: number,
};

/**
 * Search input state + debounce, shared by both presentation branches so typing
 * behavior can't drift between EUI and MUI.
 */
export function useComboBoxSearch( { setSearchString, debounceDelayMs }: ComboBoxSearchArgs ) {
	const [ inputVal, setInputVal ] = useState( '' )

	useInputDebounce( inputVal, debounceDelayMs ?? 0, ( debouncedInputValue: string ) => setSearchString?.( debouncedInputValue ) )

	return { inputVal, setInputVal }
}

export type ComboBoxStateArgs<T> = {
	data?: T[],
	getName: ( option: T ) => string,
} & ComboBoxSearchArgs;

/** Search state plus the flat `{ label, value }` option list. */
export default function useComboBoxState<T>( { data, getName, setSearchString, debounceDelayMs }: ComboBoxStateArgs<T> ) {
	const { inputVal, setInputVal } = useComboBoxSearch( { setSearchString, debounceDelayMs } )

	function buildOption( option: T ): ComboBoxOption<T> {
		return {
			label: getName( option ),
			value: option,
		}
	}

	const options: ComboBoxOption<T>[] = useMemo( () => {
		return data?.map( ( item: T ) => buildOption( item ) ) ?? []
	}, [ data ] )

	return { inputVal, setInputVal, buildOption, options }
}
